"use client";

import {yupResolver} from "@hookform/resolvers/yup";
import {SelectItem} from "@nextui-org/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

import {
  NeoButton,
  NeoDatePicker,
  NeoSelect,
  NeoTextBox
} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {NeoHeader} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {FleetService} from "@/service/fleet.service";

export type FormFuel = {
  vehicleId: string;
  id?: string;
  date?: Date | null;
  fuelType: string;
  qty: number;
  totalAmount: number;
  fuelRate: number;
  fuelStationId?: string | null;
  odometerReading?: number;
  description?: string | null;
};

type FuelFormProps = {
  data: any;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
};

export const ExpenseFuelForm: React.FC<FuelFormProps> = ({data, onSubmit}) => {
  const fueltypes = useMemo(
    () => [
      {id: "1", name: "Diesel"},
      {id: "2", name: "Petrol"}
    ],
    []
  );
  const maxDate = useMemo(() => new Date(), []);
  const {filterContext} = useNeoContext();
  const [loading, setLoading] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [fuelstations, setFuelStations] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [defaultformdata, setdefaultformdata] = useState<any>({});
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const toNumOrUndef = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
      const s = v.trim().replace(/,/g, "");
      if (!s) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const odoTransform = (_: unknown, o: unknown) => {
    if (o === "" || o == null) return undefined; // optional if empty
    if (typeof o === "number") return Number.isFinite(o) ? o : NaN;

    const s = String(o).trim().replace(/,/g, "");
    // integer only: use /^\d+$/ ; for decimals (2 dp), use /^\d+(\.\d{1,2})?$/
    if (!/^\d+$/.test(s)) return NaN; // → triggers typeError
    return Number(s);
  };

  const schema = yup.object().shape({
    vehicleId: yup.string().required("Please select vehicle"),
    id: yup.string().optional(),
    date: yup
      .date()
      .transform((v, o) => {
        if (o === "" || o == null) return null;
        const d = new Date(o);
        return isNaN(d.getTime()) ? null : d;
      })
      .nullable()
      .typeError("Enter a valid date")
      .max(today, "Date cannot be in the future"),
    fuelType: yup.string().required("Please select fuel type"),
    qty: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Quantity must be a number")
      .moreThan(0, "Quantity must be greater than 0")
      .required("Please enter quantity"),
    totalAmount: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Total cost must be a number")
      .moreThan(0, "Total cost must be greater than 0")
      .required("Please enter total cost"),
    fuelRate: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Rate must be a number")
      .moreThan(0, "Rate must be greater than 0")
      .required("Please enter rate per liter"),
    fuelStationId: yup.string().nullable(),
    odometerReading: yup
      .number()
      .transform(odoTransform)
      .typeError("Odometer must be a number")
      .min(0, "Odometer cannot be negative")
      .optional(),
    description: yup.string().nullable().default("")
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: {isSubmitting, errors}
  } = useForm<FormFuel>({
    mode: "onChange",
    resolver: yupResolver<FormFuel>(schema),
    shouldUnregister: true
  });

  const mapApiToForm = (apiData: any): FormFuel => {
    return {
      vehicleId: apiData?.vehicleId ?? "",
      date: apiData?.date,
      fuelType: apiData?.fuelType ?? "",
      qty: toNumOrUndef(apiData?.qty) ?? 0,
      totalAmount: toNumOrUndef(apiData?.totalAmount) ?? 0,
      fuelRate: toNumOrUndef(apiData?.fuelRate) ?? 0,
      fuelStationId: apiData?.fuelStationId ?? "",
      odometerReading: toNumOrUndef(apiData?.odometerReading) ?? 0,
      description: apiData?.description ?? ""
    };
  };

  const handleApiResponse = (apiResponse: any) => {
    const formValues = mapApiToForm(apiResponse);
    reset(formValues);
  };

  useEffect(() => {
    setValue("date", maxDate);
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId]);

  const fetchVehicleDatas = useCallback(async () => {
    try {
      const response = await ExpenseService.getvehicles(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        setVehicles(response.data.data);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const fetchFuelStations = useCallback(async () => {
    try {
      const response = await FleetService.getfleetdatas(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        const list = response.data.data?.["fuelStations"] ?? [];
        setFuelStations(list);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const getfueldata = useCallback(async () => {
    const requestdata = {
      expenseType: 1,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        let responsedata = response?.data?.data;
        let responsefueldata = responsedata.fuelExpense;
        setAttachments(
          Array.isArray(responsefueldata?.documents)
            ? responsefueldata.documents
            : []
        );
        let fueldata: any = {
          vehicleId: responsefueldata?.vehicleId,
          date: responsefueldata?.date,
          fuelType: responsefueldata?.fuelTypeId,
          qty: responsefueldata?.quantity,
          totalAmount: responsefueldata?.totalAmount,
          fuelRate: responsefueldata?.ratePerLiter,
          fuelStationId: responsefueldata?.fuelStationId,
          odometerReading: responsefueldata?.odometerReading,
          description: responsefueldata?.description,
          document: responsefueldata.document,
          id: data?.id
        };
        handleApiResponse(fueldata);
        setdefaultformdata(fueldata);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId, data?.id]);

  useEffect(() => {
    if (!loginUserId) return;
    (async () => {
      try {
        await fetchVehicleDatas();
        await fetchFuelStations();
        if (data?.id) {
          await getfueldata();
        }
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    })();
  }, [loginUserId, fetchVehicleDatas, fetchFuelStations]);

  const onDateChange = (name: any, event: any) => {
    clearErrors(name);
    setValue(name, event);
  };

  const onFormSubmit = useCallback(
    async (form: FormFuel) => {
      const payload = {
        ...form,
        id: data?.id ? String(data.id) : undefined,
        attachments
      };
      try {
        const result = onSubmit(payload);
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    },
    [attachments, onSubmit]
  );

  const onCancel = useCallback(() => {
    //console.log(defaultformdata);
    //handleApiResponse(defaultformdata);
    setAttachments(
      Array.isArray(defaultformdata?.documents) ? defaultformdata.documents : []
    );
  }, [reset, defaultformdata?.documents]);

  return (
    <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
      <div className="mb-5 flex-row font-bold">
        <NeoHeader message="Fuel Expense" />
      </div>

      <form
        key={data?.id ?? "new"}
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
      >
        {/* Row 1 */}
        <div className="mb-4 grid grid-cols-3 gap-2 max-sm:grid-cols-1">
          {/* Vehicle */}
          <Controller
            name="vehicleId"
            control={control}
            render={({field, fieldState}) => {
              const selectedOpt =
                vehicles.find(
                  (v: any) => String(v.id) === String(field.value)
                ) ?? null;
              return (
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="vehicleId"
                    className={`font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                  >
                    Vehicle
                  </label>
                  <Select
                    inputId="vehicleId"
                    value={selectedOpt}
                    onChange={(opt: any) => {
                      field.onChange(opt ? String(opt.id) : "");
                      clearErrors("vehicleId");
                    }}
                    options={vehicles}
                    getOptionLabel={(e: any) => e.regNo}
                    getOptionValue={(e: any) => String(e.id)}
                    placeholder="Select Vehicle"
                    styles={{
                      control: base => ({
                        ...base,
                        minHeight: "43px",
                        borderWidth: "2px",
                        borderColor: fieldState.invalid ? "#f31260" : "#d4d4d8",
                        borderRadius: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        boxShadow: "none",
                        fontSize: "14px",
                        backgroundColor: "white"
                      }),
                      indicatorSeparator: () => ({display: "none"}),
                      menu: base => ({
                        ...base,
                        borderRadius: "10px",
                        zIndex: 50
                      })
                    }}
                  />
                  {fieldState.error && (
                    <p className="text-xs text-[#f31260]" id="vehicleId-error">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              );
            }}
          />

          {/* Date */}
          <Controller
            name="date"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                label="Date"
                placeholder="Please select from date"
                errorMessage={errors.date && `${errors.date.message}`}
                maxDate={maxDate}
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("date", e);
                }}
              />
            )}
          />

          {/* Quantity */}
          <Controller
            name="qty"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Quantity (Liters)"
                id="qty"
                placeholder="0.00"
                color={errors.qty ? "danger" : "default"}
                isInvalid={!!errors.qty}
                errorMessage={errors.qty?.message as string}
                {...field}
                value={
                  field.value !== undefined && field.value !== null
                    ? String(field.value)
                    : ""
                }
              />
            )}
          />
        </div>

        {/* Row 2 */}
        <div className="mb-4 grid grid-cols-3 gap-2 max-sm:grid-cols-1">
          {/* Total Amount */}
          <Controller
            name="totalAmount"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Total Cost"
                id="totalAmount"
                placeholder="0.00"
                color={errors.totalAmount ? "danger" : "default"}
                isInvalid={!!errors.totalAmount}
                errorMessage={errors.totalAmount?.message as string}
                {...field}
                value={
                  field.value !== undefined && field.value !== null
                    ? String(field.value)
                    : ""
                }
              />
            )}
          />

          {/* Fuel Rate */}
          <Controller
            name="fuelRate"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Rate per liter"
                id="fuelRate"
                placeholder="0.00"
                color={errors.fuelRate ? "danger" : "default"}
                isInvalid={!!errors.fuelRate}
                errorMessage={errors.fuelRate?.message as string}
                {...field}
                value={
                  field.value !== undefined && field.value !== null
                    ? String(field.value)
                    : ""
                }
              />
            )}
          />

          {/* Fuel Type */}
          <Controller
            name="fuelType"
            control={control}
            render={({field, fieldState}) => {
              const currentId = fueltypes.find(
                f => String(f.id) === String(field.value)
              )?.id;
              const selectedKeys = currentId
                ? new Set<string>([currentId])
                : new Set<string>();
              return (
                <NeoSelect
                  label="Fuel Type"
                  id="fuelType"
                  placeholder="Please select fuel type"
                  color={fieldState.invalid ? "danger" : "default"}
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message as string}
                  selectedKeys={selectedKeys}
                  selectionMode="single"
                  onSelectionChange={keys => {
                    const id = Array.from(keys as Set<React.Key>)[0] as
                      | string
                      | undefined;
                    field.onChange(id ?? "");
                  }}
                >
                  {fueltypes.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </NeoSelect>
              );
            }}
          />
        </div>

        {/* Row 3 */}
        <div className="mb-4 grid grid-cols-3 gap-2 max-sm:grid-cols-1">
          {/* Fuel Station */}
          <Controller
            name="fuelStationId"
            control={control}
            render={({field, fieldState}) => {
              const valueId =
                field.value == null || field.value === ""
                  ? null
                  : String(field.value);
              const selectedOption = valueId
                ? (fuelstations.find((o: any) => String(o.id) === valueId) ??
                  null)
                : null;
              return (
                <div className="flex flex-col gap-1">
                  <label htmlFor="fuelStationId">Fuel Station</label>
                  <Select
                    inputId="fuelStationId"
                    name={field.name}
                    value={selectedOption}
                    options={fuelstations}
                    onChange={(opt: any) =>
                      field.onChange(opt ? String(opt.id) : null)
                    }
                    getOptionLabel={(e: any) => e.name}
                    getOptionValue={(e: any) => String(e.id)}
                    placeholder="Select fuel station"
                    styles={{
                      control: base => ({
                        ...base,
                        minHeight: "43px",
                        borderWidth: "2px",
                        borderColor: "#d4d4d8",
                        borderRadius: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        boxShadow: "none",
                        fontSize: "14px",
                        backgroundColor: "white"
                      }),
                      indicatorSeparator: () => ({display: "none"}),
                      menu: base => ({
                        ...base,
                        borderRadius: "10px",
                        zIndex: 50
                      })
                    }}
                  />
                </div>
              );
            }}
          />

          {/* Odometer */}
          <Controller
            name="odometerReading"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Odometer Reading"
                id="odometerReading"
                placeholder="12345"
                color={errors.odometerReading ? "danger" : "default"}
                isInvalid={!!errors.odometerReading}
                errorMessage={errors.odometerReading?.message as string}
                {...field}
                value={
                  field.value !== undefined && field.value !== null
                    ? String(field.value)
                    : ""
                }
              />
            )}
          />

          {/* Note */}
          <Controller
            name="description"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Note"
                id="description"
                placeholder="Add note"
                {...field}
                value={field.value ?? ""}
              />
            )}
          />
        </div>

        {/* Attachments */}
        {!data?.id && (
          <>
            <div className="mb-4 grid grid-cols-1">
              <UploadFormField
                value={attachments}
                onChangeFile={setAttachments}
                max={3}
                onError={msg => console.error(msg)}
              />
            </div>
            <hr />
          </>
        )}
        {/* Actions */}
        <div className="flex items-center justify-end gap-2 p-4">
          <NeoButton type="button" color="white" onClick={onCancel}>
            Cancel
          </NeoButton>
          <NeoButton
            type="submit"
            disabled={isSubmitting || loading}
            isLoading={isSubmitting || loading}
            spinner={
              <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
            }
            color="black"
          >
            Save
          </NeoButton>
        </div>
      </form>
    </div>
  );
};
